// Virtual assistant offer on the guest pages.
//
// The scan token carries `assistantMode`, the EFFECTIVE per-code setting ('off' when the
// code's facade has no assistant or the service is not configured):
//   - 'choice':   the scan landing offers the assistant or a call to reception before
//                 anything else;
//   - 'fallback': the "we could not assist you" page offers the assistant next to the
//                 callback form. 'choice' includes it: a guest who picked reception and
//                 found nobody gets the assistant there too.
// Never offered inside a kiosk's call window (the kiosk has its own assistant), nor to a
// guest who has just come back from the assistant asking for a person.
import { URLS } from './config'

export type AssistantMode = 'off' | 'fallback' | 'choice'

/** The screen asking whether to offer the assistant. */
export type AssistantScreen = 'landing' | 'unavailable'

export type AssistantContext = {
  mode: AssistantMode
  returnedFromAssistant: boolean
  embeddedInKiosk: boolean
}

/** Missing or unknown claim = 'off' (a scan token issued before the setting existed). */
export const readAssistantMode = (claims: unknown): AssistantMode => {
  const mode = claims && typeof claims === 'object' ? (claims as Record<string, unknown>).assistantMode : undefined
  return mode === 'fallback' || mode === 'choice' ? mode : 'off'
}

export const offersAssistant = (screen: AssistantScreen, context: AssistantContext): boolean => {
  if (context.returnedFromAssistant || context.embeddedInKiosk) return false
  return screen === 'landing' ? context.mode === 'choice' : context.mode !== 'off'
}

export type ScanTokens = { token: string; details: string }

// THE TWO TOKENS OF THE SCAN ROUTE (#/user-scan-qrcode/<token>/<details>?…), AS KEPT BY THE
// LANDING IN sessionStorage FOR THE SCREENS THAT NO LONGER CARRY THEM IN THEIR ROUTE.
export const scanTokensFromHash = (hash: string | null | undefined): ScanTokens | null => {
  if (!hash) return null
  const path = hash.replace(/^#/, '').split('?')[0]
  const match = /^\/user-scan-qrcode\/([^/]+)\/([^/]+)\/?$/.exec(path)
  if (!match || match[1] === 'qrcode_expired') return null
  return { token: decodeURIComponent(match[1]), details: decodeURIComponent(match[2]) }
}

const REQUEST_TIMEOUT_MS = 15000
const AVAILABILITY_TIMEOUT_MS = 6000

/**
 * Whether the assistant can take this guest now (it may have no credit left or be down): asked before offering
 * it, so the page says it is unavailable instead of failing after a tap. Any failure reads as unavailable.
 */
export const checkAssistant = async (token: string, details: string): Promise<boolean> => {
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), AVAILABILITY_TIMEOUT_MS)
  try {
    const response = await fetch(`${URLS.BASE_URL}/qrcodes/assistant-availability`, {
      method: 'POST',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify({ token, details }),
      signal: controller.signal,
    })
    if (!response.ok) return false
    const body = await response.json()
    return body?.available === true
  } catch {
    return false
  } finally {
    clearTimeout(timer)
  }
}

/**
 * Opens an assistant session for this scan and leaves the caller for it. Resolves with
 * the session URL once the browser has been sent there; rejects on any failure (the
 * caller shows one generic message and keeps its normal flow).
 */
export const openAssistant = async (token: string, details: string, language: string): Promise<string> => {
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS)
  try {
    const response = await fetch(`${URLS.BASE_URL}/qrcodes/assistant-session`, {
      method: 'POST',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify({ token, details, language }),
      signal: controller.signal,
    })
    if (!response.ok) throw new Error(`assistant session refused (${response.status})`)
    const body = await response.json()
    const url = new URL(String(body?.url ?? ''))
    if (url.protocol !== 'https:' && url.protocol !== 'http:') throw new Error('assistant session url rejected')
    window.location.assign(url.href)
    return url.href
  } finally {
    clearTimeout(timer)
  }
}
